import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Circle, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../api/client';
import './GeofenceSetup.css';

interface Geofence {
  id: string;
  device_id: string;
  name: string;
  center_lat: number;
  center_lon: number;
  radius_m: number;
}

function formatRadius(m: number): string {
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${m} m`;
}

function ClickHandler({ onMapClick }: { onMapClick: (lat: number, lon: number) => void }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export default function GeofenceSetup() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();

  const [existing, setExisting] = useState<Geofence | null>(null);
  const [center, setCenter] = useState<[number, number] | null>(null);
  const [radius, setRadius] = useState(500);
  const [name, setName] = useState('Strefa bezpieczna');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState('');
  const [mapCenter, setMapCenter] = useState<[number, number]>([52.2297, 21.0122]);

  useEffect(() => {
    if (!deviceId) return;
    api.get(`/geofences/${deviceId}`)
      .then((r) => {
        if (r.data.length > 0) {
          const g: Geofence = r.data[0];
          setExisting(g);
          setCenter([g.center_lat, g.center_lon]);
          setRadius(g.radius_m);
          setName(g.name);
          setMapCenter([g.center_lat, g.center_lon]);
        } else {
          // No geofence yet — center on the vehicle's current position instead of Warsaw.
          api.get(`/devices/${deviceId}/last-position`)
            .then((pr) => {
              if (pr.data) setMapCenter([pr.data.latitude, pr.data.longitude]);
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [deviceId]);

  const handleMapClick = useCallback((lat: number, lon: number) => {
    setCenter([lat, lon]);
    setMsg('');
  }, []);

  const handleSave = async () => {
    if (!center) { setMsg('Kliknij na mapę, aby ustawić centrum strefy.'); return; }
    setSaving(true);
    setMsg('');
    try {
      await api.post(`/geofences/${deviceId}`, {
        name,
        center_lat: center[0],
        center_lon: center[1],
        radius_m: radius,
      });
      setMsg('Strefa zapisana.');
      // Refresh existing
      const r = await api.get(`/geofences/${deviceId}`);
      if (r.data.length > 0) setExisting(r.data[0]);
    } catch {
      setMsg('Błąd zapisu. Spróbuj ponownie.');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!existing) return;
    setDeleting(true);
    setMsg('');
    try {
      await api.delete(`/geofences/${deviceId}`);
      setExisting(null);
      setCenter(null);
      setMsg('Strefa usunięta.');
    } catch {
      setMsg('Błąd usuwania.');
    }
    setDeleting(false);
  };

  return (
    <div className="geofence-page">
      <div className="geofence-header">
        <button className="geofence-back" onClick={() => navigate('/account')}>← Wróć</button>
        <h2>Strefa bezpieczna</h2>
      </div>

      <div className="geofence-hint">
        Kliknij na mapę, aby ustawić centrum strefy. Przeciągnij suwak, żeby zmienić promień.
      </div>

      <div className="geofence-map-wrap">
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          key={mapCenter.join(',')}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />
          <ClickHandler onMapClick={handleMapClick} />
          {center && (
            <Circle
              center={center}
              radius={radius}
              pathOptions={{ color: '#e63946', fillColor: '#e63946', fillOpacity: 0.15, weight: 2 }}
            />
          )}
        </MapContainer>
      </div>

      <div className="geofence-controls">
        <div className="geofence-row">
          <label>Nazwa strefy</label>
          <input
            className="geofence-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
          />
        </div>

        <div className="geofence-row">
          <label>Promień: <strong>{formatRadius(radius)}</strong></label>
          <input
            type="range"
            min={50}
            max={50000}
            step={50}
            value={radius}
            onChange={(e) => setRadius(Number(e.target.value))}
            className="geofence-slider"
          />
          <div className="geofence-slider-labels">
            <span>50 m</span><span>50 km</span>
          </div>
        </div>

        {center ? (
          <div className="geofence-coords">
            Centrum: {center[0].toFixed(5)}, {center[1].toFixed(5)}
          </div>
        ) : (
          <div className="geofence-coords muted">Brak wybranego centrum — kliknij na mapę</div>
        )}

        {msg && <div className={`geofence-msg ${msg.includes('Błąd') ? 'error' : 'ok'}`}>{msg}</div>}

        <button className="geofence-btn-save" onClick={handleSave} disabled={saving || !center}>
          {saving ? 'Zapisywanie…' : 'Zapisz strefę'}
        </button>

        {existing && (
          <button className="geofence-btn-delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Usuwanie…' : 'Usuń strefę'}
          </button>
        )}
      </div>
    </div>
  );
}
